import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, TrashIcon, PlusIcon, MoveUpIcon, MoveDownIcon, ImageIcon, TypeIcon, BoldIcon, ItalicIcon, UnderlineIcon, ListIcon, AlignLeftIcon, AlignCenterIcon, AlignRightIcon, LinkIcon, ChevronDownIcon } from 'lucide-react';
interface TemplateBlock {
  id: number;
  type: 'header' | 'body' | 'footer';
  title?: string;
  content: string;
  imageUrl?: string;
  buttonText?: string;
  buttonUrl?: string;
  alignment?: 'left' | 'center' | 'right';
}
const TemplateEditor = () => {
  const {
    id
  } = useParams();
  const isEditing = !!id;
  const [templateName, setTemplateName] = useState(isEditing ? 'Monthly Update' : '');
  const [templateDescription, setTemplateDescription] = useState(isEditing ? 'Standard template for monthly newsletters' : '');
  const [isActive, setIsActive] = useState(true);
  const [predefinedReportType, setPredefinedReportType] = useState('');
  const [showPlaceholderMenu, setShowPlaceholderMenu] = useState(false);
  const [blocks, setBlocks] = useState<TemplateBlock[]>([{
    id: 1,
    type: 'header',
    title: 'Monthly Newsletter',
    content: 'Your monthly update from ekseba Uddokta',
    alignment: 'center'
  }, {
    id: 2,
    type: 'body',
    title: 'Welcome Message',
    content: 'Dear {{EntrepreneurName}},\n\nWelcome to our monthly newsletter...',
    alignment: 'left'
  }, {
    id: 3,
    type: 'footer',
    content: 'Contact us at info@ekseba.com | Dhaka, Bangladesh\nUnsubscribe link',
    alignment: 'center'
  }]);
  const deleteBlock = (blockId: number) => {
    setBlocks(blocks.filter(b => b.id !== blockId));
  };
  const moveBlockUp = (index: number) => {
    if (index <= 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index - 1]] = [newBlocks[index - 1], newBlocks[index]];
    setBlocks(newBlocks);
  };
  const moveBlockDown = (index: number) => {
    if (index >= blocks.length - 2) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    setBlocks(newBlocks);
  };
  const updateBlock = (blockId: number, updates: Partial<TemplateBlock>) => {
    setBlocks(blocks.map(b => b.id === blockId ? {
      ...b,
      ...updates
    } : b));
  };
  const insertPlaceholder = (placeholder: string) => {
    // In a real implementation, this would insert at cursor position
    console.log('Insert placeholder:', placeholder);
    setShowPlaceholderMenu(false);
  };
  return <div className="min-h-screen bg-secondary-50" data-id="element-550">
      <div className="max-w-7xl mx-auto px-4 py-6" data-id="element-551">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" data-id="element-552">
          <div className="flex items-center" data-id="element-553">
            <Link to="/templates" className="p-2 mr-3 rounded-md text-secondary-500 hover:bg-white transition-colors" data-id="element-554">
              <ArrowLeftIcon size={20} data-id="element-555" />
            </Link>
            <div data-id="element-556">
              <h1 className="text-2xl font-bold text-secondary-900" data-id="element-557">
                {isEditing ? 'Edit Template' : 'Create Template'}
              </h1>
              <p className="text-sm text-secondary-500 mt-1" data-id="element-558">
                Design your email newsletter template
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-559">
          {/* Left Sidebar - Template Settings */}
          <div className="lg:col-span-1 space-y-4" data-id="element-560">
            <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-5" data-id="element-561">
              <h3 className="text-sm font-semibold text-secondary-900 mb-4 uppercase tracking-wide" data-id="element-562">
                Template Settings
              </h3>
              <div className="space-y-4" data-id="element-563">
                <div data-id="element-564">
                  <label htmlFor="templateName" className="block text-sm font-medium text-secondary-700 mb-1.5" data-id="element-565">
                    Template Name *
                  </label>
                  <input id="templateName" type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Monthly Newsletter – General" className="input text-sm" required data-id="element-566" />
                </div>
                <div data-id="element-567">
                  <label htmlFor="templateDescription" className="block text-sm font-medium text-secondary-700 mb-1.5" data-id="element-568">
                    Description
                  </label>
                  <textarea id="templateDescription" value={templateDescription} onChange={e => setTemplateDescription(e.target.value)} placeholder="Template for regular monthly updates..." rows={3} className="input text-sm" data-id="element-569" />
                </div>
                <div data-id="element-570">
                  <label className="block text-sm font-medium text-secondary-700 mb-2" data-id="element-571">
                    Status
                  </label>
                  <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-md" data-id="element-572">
                    <span className="text-sm text-secondary-700" data-id="element-573">
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                    <button type="button" className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${isActive ? 'bg-green-600' : 'bg-secondary-300'}`} onClick={() => setIsActive(!isActive)} data-id="element-574">
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActive ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-575" />
                    </button>
                  </div>
                  {!isActive && <p className="text-xs text-secondary-500 mt-1.5" data-id="element-576">
                      Inactive templates cannot be used to create newsletters
                    </p>}
                </div>
                <div data-id="element-577">
                  <label htmlFor="predefinedReportType" className="block text-sm font-medium text-secondary-700 mb-1.5" data-id="element-578">
                    Predefined Report Type
                  </label>
                  <select id="predefinedReportType" value={predefinedReportType} onChange={e => setPredefinedReportType(e.target.value)} className="input text-sm" data-id="element-579">
                    <option value="" data-id="element-580">Select report type...</option>
                    <option value="type1" data-id="element-581">Report Type 1</option>
                    <option value="type2" data-id="element-582">Report Type 2</option>
                    <option value="type3" data-id="element-583">Report Type 3</option>
                  </select>
                  <p className="text-xs text-secondary-500 mt-1.5" data-id="element-584">
                    Optional: Select one of the standard report layouts
                  </p>
                </div>
              </div>
            </div>
            {/* Placeholder Variables */}
            <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-5" data-id="element-585">
              <h3 className="text-sm font-semibold text-secondary-900 mb-3 uppercase tracking-wide" data-id="element-586">
                Insert Placeholder
              </h3>
              <p className="text-xs text-secondary-500 mb-3" data-id="element-587">
                Click to insert dynamic variables
              </p>
              <div className="space-y-2" data-id="element-588">
                <button onClick={() => insertPlaceholder('{{EntrepreneurName}}')} className="w-full text-left px-3 py-2 text-sm bg-secondary-50 hover:bg-primary-50 hover:text-primary-700 rounded-md transition-colors" data-id="element-589">
                  {'{{EntrepreneurName}}'}
                </button>
                <button onClick={() => insertPlaceholder('{{Date}}')} className="w-full text-left px-3 py-2 text-sm bg-secondary-50 hover:bg-primary-50 hover:text-primary-700 rounded-md transition-colors" data-id="element-590">
                  {'{{Date}}'}
                </button>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-5 space-y-3" data-id="element-591">
              <button className="btn btn-primary w-full flex items-center justify-center" data-id="element-592">
                <SaveIcon size={16} className="mr-2" data-id="element-593" />
                Save Template
              </button>
              <Link to="/templates" className="btn btn-secondary w-full flex items-center justify-center" data-id="element-594">
                Cancel
              </Link>
            </div>
          </div>
          {/* Main Canvas - Template Editor */}
          <div className="lg:col-span-3" data-id="element-595">
            <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6" data-id="element-596">
              <div className="flex items-center justify-between mb-6" data-id="element-597">
                <h3 className="text-lg font-semibold text-secondary-900" data-id="element-598">
                  Template Layout
                </h3>
                <span className="text-xs text-secondary-500 bg-secondary-100 px-3 py-1 rounded-full" data-id="element-599">
                  WYSIWYG Editor
                </span>
              </div>
              {/* Email Canvas */}
              <div className="max-w-3xl mx-auto border-2 border-secondary-200 rounded-lg overflow-hidden bg-white" data-id="element-600">
                {blocks.map((block, index) => <div key={block.id} className="relative group" data-id="element-601">
                    {/* Section Label & Controls */}
                    <div className="absolute -left-32 top-4 hidden lg:flex items-center space-x-2" data-id="element-602">
                      <span className="text-xs font-medium text-secondary-500 uppercase tracking-wide" data-id="element-603">
                        {block.type === 'header' && 'Header'}
                        {block.type === 'body' && `Body ${index}`}
                        {block.type === 'footer' && 'Footer'}
                      </span>
                      {block.type === 'body' && <div className="flex items-center space-x-1" data-id="element-604">
                          <button onClick={() => moveBlockUp(index)} disabled={index <= 1} className="p-1 rounded text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 disabled:opacity-30" title="Move up" data-id="element-605">
                            <MoveUpIcon size={14} data-id="element-606" />
                          </button>
                          <button onClick={() => moveBlockDown(index)} disabled={index >= blocks.length - 2} className="p-1 rounded text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 disabled:opacity-30" title="Move down" data-id="element-607">
                            <MoveDownIcon size={14} data-id="element-608" />
                          </button>
                          <button onClick={() => deleteBlock(block.id)} className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50" title="Delete block" data-id="element-609">
                            <TrashIcon size={14} data-id="element-610" />
                          </button>
                        </div>}
                    </div>
                    {/* Block Content */}
                    <div className={`border-b border-secondary-200 p-8 ${block.type === 'header' ? 'bg-gradient-to-b from-primary-50 to-white' : block.type === 'footer' ? 'bg-secondary-50' : 'bg-white'}`} data-id="element-611">
                      {/* Header Block */}
                      {block.type === 'header' && <div className="text-center space-y-4" data-id="element-612">
                          <div className="flex justify-center mb-4" data-id="element-613">
                            <div className="w-20 h-20 bg-primary-600 rounded-lg flex items-center justify-center" data-id="element-614">
                              <ImageIcon size={32} className="text-white" data-id="element-615" />
                            </div>
                          </div>
                          <input type="text" value={block.title} onChange={e => updateBlock(block.id, {
                      title: e.target.value
                    })} className="text-3xl font-bold text-secondary-900 text-center w-full bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2" placeholder="Newsletter Title" data-id="element-616" />
                          <textarea value={block.content} onChange={e => updateBlock(block.id, {
                      content: e.target.value
                    })} className="text-secondary-600 text-center w-full bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 resize-none" rows={2} placeholder="Subtitle or tagline" data-id="element-617" />
                        </div>}

                      {/* Body Block */}
                      {block.type === 'body' && <div className="space-y-4" data-id="element-618">
                          <input type="text" value={block.title} onChange={e => updateBlock(block.id, {
                      title: e.target.value
                    })} className="text-xl font-semibold text-secondary-900 w-full bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2" placeholder="Section Title" data-id="element-619" />

                          {/* Rich Text Toolbar */}
                          <div className="flex items-center space-x-1 pb-2 border-b border-secondary-200" data-id="element-620">
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-621">
                              <BoldIcon size={16} data-id="element-622" />
                            </button>
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-623">
                              <ItalicIcon size={16} data-id="element-624" />
                            </button>
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-625">
                              <UnderlineIcon size={16} data-id="element-626" />
                            </button>
                            <div className="w-px h-4 bg-secondary-300 mx-1" data-id="element-627" />
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-628">
                              <ListIcon size={16} data-id="element-629" />
                            </button>
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-630">
                              <AlignLeftIcon size={16} data-id="element-631" />
                            </button>
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-632">
                              <AlignCenterIcon size={16} data-id="element-633" />
                            </button>
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-634">
                              <AlignRightIcon size={16} data-id="element-635" />
                            </button>
                            <div className="w-px h-4 bg-secondary-300 mx-1" data-id="element-636" />
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-637">
                              <ImageIcon size={16} data-id="element-638" />
                            </button>
                            <button className="p-1.5 rounded hover:bg-secondary-100 text-secondary-600" data-id="element-639">
                              <LinkIcon size={16} data-id="element-640" />
                            </button>
                          </div>

                          <textarea value={block.content} onChange={e => updateBlock(block.id, {
                      content: e.target.value
                    })} className="text-secondary-700 w-full bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 resize-none" rows={6} placeholder="Add your content here..." data-id="element-641" />

                          {/* Optional Button */}
                          <div className="pt-4 space-y-2" data-id="element-642">
                            <div className="flex items-center space-x-2" data-id="element-643">
                              <input type="text" value={block.buttonText || ''} onChange={e => updateBlock(block.id, {
                          buttonText: e.target.value
                        })} className="flex-1 text-sm px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Button text (optional)" data-id="element-644" />
                              <input type="text" value={block.buttonUrl || ''} onChange={e => updateBlock(block.id, {
                          buttonUrl: e.target.value
                        })} className="flex-1 text-sm px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Button URL" data-id="element-645" />
                            </div>
                            {block.buttonText && <div className="flex justify-center pt-2" data-id="element-646">
                                <button className="px-6 py-2 bg-primary-600 text-white rounded-md font-medium" data-id="element-647">
                                  {block.buttonText}
                                </button>
                              </div>}
                          </div>
                        </div>}

                      {/* Footer Block */}
                      {block.type === 'footer' && <div className="text-center" data-id="element-648">
                          <textarea value={block.content} onChange={e => updateBlock(block.id, {
                      content: e.target.value
                    })} className="text-sm text-secondary-600 text-center w-full bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 resize-none" rows={3} placeholder="Contact information and unsubscribe link" data-id="element-649" />
                        </div>}
                    </div>
                  </div>)}
              </div>
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-650">
                <p className="text-sm text-blue-800" data-id="element-651">
                  <strong data-id="element-652">Tip:</strong> Use the rich text toolbar to format your
                  content. Insert placeholders like {'{{EntrepreneurName}}'} to
                  personalize emails. You can reorder body sections using the
                  move controls.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default TemplateEditor;